import React, {
  createContext,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import { MuiTheme } from 'config/materialUI';
import { useTranslation } from 'react-i18next';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import styled from 'styled-components';

import { createFilterOptions } from 'utils/filterOptions';
import { getTextWidth } from 'utils/misc';

import Autocomplete, {
  AutocompleteProps,
  AutocompleteRenderOptionState
} from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';

import MuiAutocompleteOption, {
  optionFontSize,
  optionHeight,
  optionPadding
} from './MuiAutocompleteOption';

export const defaultListWidth = 300;
const scrollbarWidth = 18; // depends on the OS, at most should be 18px

const renderRow = (props: ListChildComponentProps) => {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + optionPadding
    }
  });
};

const OuterElementContext = createContext({});

const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const StyledOuterDiv = styled.div`
  li {
    border-bottom: 1px solid #eee;
  }
`;

// Adapter for react-window
const ListboxComponent = forwardRef<HTMLDivElement>((props: any, ref) => {
  const { children, listWidth, getItemSize, isCustomHeight, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const listHeight =
    (itemCount > 9 ? 10 : itemCount) *
    (isCustomHeight ? optionHeight * 2 : optionHeight);

  // Added ref for VariableSizeList
  const listRef = useRef<VariableSizeList>(null);

  // Added useEffect to trigger recalculation of item sizes
  useEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [itemData]);

  return (
    <StyledOuterDiv ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          ref={listRef}
          itemData={itemData}
          height={listHeight}
          width={listWidth}
          outerElementType={OuterElementType}
          innerElementType="ul"
          overscanCount={5}
          itemCount={itemCount}
          itemSize={
            getItemSize
              ? (index: number) => getItemSize(itemData[index])
              : () => optionHeight
          }
          estimatedItemSize={optionHeight}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </StyledOuterDiv>
  );
});

const CustomPopper = (props) => (
  <Popper {...props} style={{ width: 'auto' }} placement="bottom-start" />
);

interface VirtualizedAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  optionLabelProp?: string;
  extraWidth?: number;
  onOpenCallback?: () => void;
  renderOption?: (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
    option: T,
    state: AutocompleteRenderOptionState
  ) => ReactNode;
  getItemSize?: (
    option: React.ReactChild | React.ReactFragment | React.ReactPortal
  ) => number;
}

export default function VirtualizedAutocomplete<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(
  props: VirtualizedAutocompleteProps<
    T,
    Multiple,
    DisableClearable,
    FreeSolo
  > & { isCustomHeight?: boolean }
) {
  const { t } = useTranslation('common');
  const [listWidth, setListWidth] = useState<number | null>(null);
  const {
    options,
    optionLabelProp = 'name',
    extraWidth = 0,
    onOpenCallback,
    open,
    renderOption,
    filterOptions,
    getItemSize,
    isCustomHeight,
    ...rest
  } = props;
  const ref = useRef<HTMLDivElement>(document.createElement('div'));

  const calculateListWidth = useCallback(() => {
    const font = `${optionFontSize}px ${MuiTheme.typography.fontFamily}`;
    const maxWidth = Math.max(
      ...options.map((o) => getTextWidth(o[optionLabelProp], font))
    );
    setListWidth(
      Math.max(ref.current.offsetWidth, maxWidth + extraWidth + scrollbarWidth)
    );
  }, [extraWidth, optionLabelProp, options]);

  if (open && !listWidth) {
    calculateListWidth();
  }

  useEffect(() => {
    const resizeEvtCallback = (e: Event) => {
      if (e.isTrusted) {
        calculateListWidth();
      }
    };
    window.addEventListener('resize', resizeEvtCallback);
    return () => window.removeEventListener('resize', resizeEvtCallback);
  }, [calculateListWidth]);

  useEffect(() => {
    if (open && listWidth) {
      const input = ref.current.querySelector('input');
      if (input) {
        input.focus();
      }
    }
  }, [listWidth, open]);

  const onOpen = () => {
    if (onOpenCallback) {
      onOpenCallback();
    }
    if (!listWidth) {
      calculateListWidth();
    }
  };

  const defaultRenderOption = (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
    option: T,
    state: AutocompleteRenderOptionState
  ) => {
    if (!option[optionLabelProp]) {
      throw new Error(
        `Option does not have a '${optionLabelProp}' property, if you need a custom option structure please pass an optionLabelProp`
      );
    }
    return (
      <MuiAutocompleteOption
        {...props}
        inputValue={state.inputValue}
        selected={state.selected}
        label={option[optionLabelProp]}
      />
    );
  };

  return (
    <Autocomplete
      {...rest}
      options={options}
      noOptionsText={t('noOptions')}
      PopperComponent={CustomPopper}
      ListboxComponent={
        ListboxComponent as React.ComponentType<
          React.HTMLAttributes<HTMLElement>
        >
      }
      ListboxProps={
        {
          listWidth: listWidth || defaultListWidth,
          getItemSize: getItemSize,
          isCustomHeight: isCustomHeight
        } as any
      }
      onOpen={onOpen}
      open={open}
      ref={ref}
      renderOption={renderOption || defaultRenderOption}
      filterOptions={
        filterOptions || createFilterOptions(optionLabelProp, [optionLabelProp])
      }
    />
  );
}
